import { feature as turfFeature, featureCollection } from '@turf/helpers';
import { centerOfMass } from '@turf/turf';
import { cn, Loader } from 'component-library';
import { Feature, Point } from 'geojson';
import { LngLatLike, Point as MapboxPoint } from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GeoJSONSource, Layer, LayerProps, MapLayerMouseEvent, MapRef, Marker, Source } from 'react-map-gl';

import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import { Plot, PlotType } from '@/api/rest/resources/types/plot';
import { Form, Map, withSuspenseBoundary } from '@/components';
import { MapSettings } from '@/components/MapOverview/components/MapSettings';
import { useBoundingBox } from '@/components/MapOverview/hooks/useBoundingBox';
import { usePlotGroupWithoutGrouping } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { LayerProps as HeatMapLayerProps } from '@/components/widgets/o/Map/constants';
// eslint-disable-next-line import/no-cycle
import { HeatMapLayer } from '@/components/widgets/o/Map/HeatMapLayer';
import {
  DEFAULT_MAP_BACKGROUND,
  DEFAULT_PLOT_OVERVIEW_SHAPE,
  getDefaultOpacityByLayer,
  PLOT_OVERVIEW_SHAPE,
  PlotOverviewShape,
  useControlsForm,
} from '@/components/widgets/o/Map/hooks/useFormControls';
import {
  DEFAULT_MAP_STYLE,
  FEATURE_FLAG_ENABLE_AUTO_PLOT_DETECTION,
  MAP_MAX_ZOOM,
  MAP_OVERVIEW_PADDING,
} from '@/config/constants';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useAllPlotsForOrganization } from '@/pages/shared/hooks/usePlotsForOrganization';
import { getGeometriesForPlots } from '@/utils/bounds';
import { getPNGIconForLandType } from '@/utils/getPNGIconForLandType';
import { isTestEnv } from '@/utils/isTestEnv';
import { getColorOfPlotType } from '@/utils/plot';

import { ProjectMapCoordinatePopup } from '../../pages/project/pages/sustainableProduction/components/ProjectMapCoordinatesPopup';
import { ProjectMapPopup } from '../../pages/project/pages/sustainableProduction/components/ProjectMapPopup';

const MARKERS_SOURCE_ID = 'plot-markers';
const POLYGONS_SOURCE_ID = 'plot-polygons';

export const PlotsMap = (props: {
  organizationIdArg?: string;
  setSelectedPlotIds?: (ids: string[]) => void;
  selectedPlotIds?: string[];
  layers?: HeatMapLayerProps[];
  styles?: Record<string, string>;
}) => {
  return <ProjectsMap {...props} />;
};

export const ProjectsMap = withSuspenseBoundary(
  (props: {
    organizationIdArg?: string;
    setSelectedPlotIds?: (ids: string[]) => void;
    selectedPlotIds?: string[];
    layers?: HeatMapLayerProps[];
    styles?: Record<string, string>;
  }) => {
    return <ProjectsMapBase {...props} />;
  },
  <Loader className='min-h-[500px] relative' />,
);

const ProjectsMapBase = ({
  organizationIdArg,
  setSelectedPlotIds,
  selectedPlotIds,
  layers,
  styles,
}: {
  organizationIdArg?: string;
  setSelectedPlotIds?: (ids: string[]) => void;
  selectedPlotIds?: string[];
  layers?: HeatMapLayerProps[];
  styles?: Record<string, string>;
}) => {
  const isLargeScreen = useScreenSize() === 'large';

  const plotGroupId = usePlotGroupWithoutGrouping()?.id;

  const allPlots: Plot[] = useAllPlotsForOrganization(
    organizationIdArg,
    { fact_names: 'country_name' },
    plotGroupId ?? undefined,
    undefined,
  ).data.results;
  const plots = useMemo(() => allPlots.filter((plot) => plot.type !== PlotType.UNDEFINED), [allPlots]);

  const [selectedPlot, setSelectedPlot] = useState<Plot | null>(null);
  const [clickedPoint, setClickedPoint] = useState<{ coords: [number, number] } | null>(null);

  // Build GeoJSON feature collections for clustering and polygons
  const plotMarkers = featureCollection(
    plots.map((plot, index) => ({
      ...centerOfMass(plot.multi_polygon, {
        properties: {
          plot,
          name: plot.name,
          landType: plot.type,
          icon: `${plot.type}-icon`,
          selected: selectedPlotIds?.includes(plot.id) ?? false,
        },
      }),
      id: index + 1,
    })),
  );
  const plotPolygons = featureCollection(
    plots
      .filter((plot) => plot.approximation_method !== 'circle')
      .map((plot, index) =>
        turfFeature(plot.multi_polygon, { plot, color: getColorOfPlotType(plot.type) }, { id: index + 1 }),
      ),
  );

  const controlsForm = useControlsForm({
    defaultValues: {
      year: `2020`,
      layers: [],
      habitatIntactnessThreats: [],
      layerOpacity: getDefaultOpacityByLayer(),
      mapBackground: DEFAULT_MAP_BACKGROUND,
      plotOverviewShape: DEFAULT_PLOT_OVERVIEW_SHAPE,
    },
  });

  const [isMapReady, setIsMapReady] = useState(isTestEnv);
  const [mapStyle, setMapStyle] = useState(DEFAULT_MAP_STYLE);

  const [plotOverviewShape, selectedLayers] = controlsForm.watch(['plotOverviewShape', 'layers']);
  const showFillLayer = plotOverviewShape === PLOT_OVERVIEW_SHAPE.polygon;

  const geometries = useMemo(() => getGeometriesForPlots(plots), [plots]);
  const mapRef = useRef<MapRef | null>(null);

  const { bounds } = useBoundingBox({ geometries, padding: MAP_OVERVIEW_PADDING });

  useEffect(() => {
    if (isMapReady && typeof mapRef?.current?.fitBounds === 'function') {
      // Ensure that the map fits the bounds of the plots on load and when the plots change
      mapRef.current.fitBounds(bounds, {
        padding: MAP_OVERVIEW_PADDING,
        maxZoom: MAP_MAX_ZOOM,
      });
    }
  }, [bounds, isMapReady]);

  const mapRefCallback = useCallback((ref: MapRef | null) => {
    if (ref !== null) {
      mapRef.current = ref;
      const map = ref;
      Object.values(PlotType).forEach((plotType: PlotType) => {
        if (!map.hasImage?.(`${plotType}-icon`)) {
          map.loadImage?.(getPNGIconForLandType(plotType), (error, image) => {
            if (error || image === undefined) throw error;
            map.addImage(`${plotType}-icon`, image, { sdf: true });
          });
        }
      });
    }
  }, []);

  // Cluster and marker click/hover handlers
  const handleClusterMarkerClick = (feature: Feature<Point>, clusterId: number) => {
    if (!mapRef.current) return;
    const mapboxSource = mapRef.current.getSource(MARKERS_SOURCE_ID) as GeoJSONSource;
    mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;
      if (!mapRef.current) return;
      mapRef.current.easeTo({
        center: feature.geometry.coordinates as LngLatLike,
        zoom,
        duration: 500,
      });
    });
  };

  const handlePolygonOrUnclusteredMarkerClick = (feature: Feature) => {
    if (!isLargeScreen) return;

    const plot = JSON.parse(feature.properties?.plot) as Plot | undefined;

    if (!plot) return;
    if (plot.id === selectedPlot?.id) {
      setSelectedPlot(null);
      if (setSelectedPlotIds) {
        const selectedIds = selectedPlotIds ? selectedPlotIds.filter((id) => id !== plot.id) : [];
        setSelectedPlotIds(selectedIds);
      }
      return;
    }

    const plotWithProjectId = plots.find((p) => p.id === plot.id);
    if (plotWithProjectId) {
      setSelectedPlot(plotWithProjectId);
      if (setSelectedPlotIds) {
        const selectedIds = selectedPlotIds ? [...selectedPlotIds, plotWithProjectId.id] : [plotWithProjectId.id];
        setSelectedPlotIds(selectedIds);
      }
    }

    setTimeout(() => {
      const polygonCentroid = centerOfMass(plot.multi_polygon).geometry.coordinates;
      mapRef.current?.easeTo({
        center: polygonCentroid as [number, number],
        duration: 500,
        offset: new MapboxPoint(-200, 0),
      });
    }, 200);
  };

  const handleMapClick = (event: MapLayerMouseEvent) => {
    if (selectedLayers.some((layer) => layer.includes(MAP_LAYER_FACTS.in_situ_map_layer))) {
      // Prevent UX where user clicks the layer markers and the plot popup opens
      return;
    }
    const feature = event.features?.[0];
    if (!feature) {
      // TODO: Feature flag to be removed on release of MVP-5233
      if (FEATURE_FLAG_ENABLE_AUTO_PLOT_DETECTION) {
        const lngLat = [event.lngLat.lng, event.lngLat.lat] as [number, number];
        setClickedPoint({ coords: lngLat });
      }
      return;
    }
    const clusterId = feature.properties?.cluster_id;
    if (clusterId) {
      handleClusterMarkerClick(feature as Feature<Point>, clusterId);
    } else {
      handlePolygonOrUnclusteredMarkerClick(feature);
    }
  };

  const hoveredPolygonIdRef = useRef<number | null>(null);
  const handleMapMouseMove = (event: MapLayerMouseEvent) => {
    const map = mapRef.current;
    if (!map) return;
    const feature = event.features?.[0];
    if (!feature) {
      map.getCanvas().style.cursor = 'initial';
      if (hoveredPolygonIdRef.current !== null) {
        map.setFeatureState(
          { source: POLYGONS_SOURCE_ID, id: hoveredPolygonIdRef.current as number },
          { hover: false },
        );
        hoveredPolygonIdRef.current = null;
      }
    } else {
      map.getCanvas().style.cursor = 'pointer';
      if (hoveredPolygonIdRef.current !== null) {
        map.setFeatureState(
          { source: POLYGONS_SOURCE_ID, id: hoveredPolygonIdRef.current as number },
          { hover: false },
        );
      }
      hoveredPolygonIdRef.current = feature.id as number;
      map.setFeatureState({ source: POLYGONS_SOURCE_ID, id: hoveredPolygonIdRef.current as number }, { hover: true });
    }
  };

  const handleClosePopup = () => {
    setTimeout(() => {
      if (!selectedPlot) return;

      // Rettain current zoom level
      const polygonCentroid = centerOfMass(selectedPlot.multi_polygon).geometry.coordinates;
      mapRef.current?.easeTo({
        center: polygonCentroid as [number, number],
        duration: 200,
        offset: new MapboxPoint(-200, 0),
      });
    }, 10);
    setSelectedPlot(null);
  };

  return (
    <div className={cn('relative h-full min-h-[500px] w-full', styles?.root)}>
      <div className='relative h-full w-full rounded-3xl bg-primary-50' data-testid='projects-map'>
        <Form {...controlsForm}>
          <Map
            ref={mapRefCallback}
            cooperativeGestures
            data-testid='project-map-gl'
            initialViewState={{
              bounds,
              fitBoundsOptions: { padding: MAP_OVERVIEW_PADDING, maxZoom: MAP_MAX_ZOOM },
            }}
            onLoad={() => setIsMapReady(true)}
            interactiveLayerIds={[
              clusterLayer.id as string,
              unclusteredPointLayer.id as string,
              polygonFillLayer.id as string, // if you want polygons clickable
            ]}
            onClick={handleMapClick}
            onMouseMove={handleMapMouseMove}
            style={{ opacity: isMapReady ? 1 : 0, transition: 'opacity 0.2s', borderRadius: 24 }}
            mapStyle={mapStyle}
          >
            <Source id={POLYGONS_SOURCE_ID} type='geojson' data={plotPolygons}>
              <Layer {...getPolygonOutlineLayerDark(plotOverviewShape)} />
              <Layer {...getPolygonOutlineLayerLight(plotOverviewShape)} />
              {showFillLayer && <Layer {...polygonFillLayer} />}
            </Source>
            <HeatMapLayer setMapStyle={setMapStyle} layers={layers} />
            <MapSettings bounds={bounds} mapRef={mapRef.current} />
            <Source id={MARKERS_SOURCE_ID} type='geojson' data={plotMarkers} cluster={true}>
              <Layer {...clusterLayer} />
              <Layer {...clusterCountLayer} />
              <Layer {...unclusteredPointLayer} />
              <Layer {...iconLayer} />
            </Source>
            {selectedPlot && <ProjectMapPopup plot={selectedPlot} onClose={handleClosePopup} />}
            {clickedPoint && (
              <>
                <Marker longitude={clickedPoint.coords[0]} latitude={clickedPoint.coords[1]} anchor='bottom'>
                  <div className='w-4 h-4 bg-red-600 border-2 border-white rounded-full shadow' />
                </Marker>
                <ProjectMapCoordinatePopup
                  lat={clickedPoint.coords[1]}
                  lng={clickedPoint.coords[0]}
                  onClose={() => setClickedPoint(null)}
                />
              </>
            )}
          </Map>
        </Form>
      </div>
    </div>
  );
};

const POLYGON_BORDER_FOR_PLOT_OVERVIEW_BORDER = {
  'line-width': 6,
  'line-opacity': 1,
  'line-color': 'rgb(255, 255, 0)',
} as const;

const getPolygonOutlineLayerLight = (plotOverviewShape: PlotOverviewShape): LayerProps => {
  switch (plotOverviewShape) {
    case PLOT_OVERVIEW_SHAPE.border:
      return {
        id: 'feat-line',
        type: 'line',
        source: POLYGONS_SOURCE_ID,
        paint: POLYGON_BORDER_FOR_PLOT_OVERVIEW_BORDER,
      };
    case PLOT_OVERVIEW_SHAPE.polygon:
    default:
      return {
        id: 'feat-line',
        type: 'line',
        source: POLYGONS_SOURCE_ID,
        paint: {
          'line-width': 3,
          'line-opacity': 0.6,
          'line-color': 'rgb(255, 255, 255)',
        },
      };
  }
};

const getPolygonOutlineLayerDark = (plotOverviewShape: PlotOverviewShape): LayerProps => {
  switch (plotOverviewShape) {
    case PLOT_OVERVIEW_SHAPE.border:
      return {
        id: 'feat-line',
        type: 'line',
        source: POLYGONS_SOURCE_ID,
        paint: POLYGON_BORDER_FOR_PLOT_OVERVIEW_BORDER,
      };
    case PLOT_OVERVIEW_SHAPE.polygon:
    default:
      return {
        id: 'feat-line',
        type: 'line',
        source: POLYGONS_SOURCE_ID,
        paint: {
          'line-width': 3,
          'line-opacity': 0.6,
          'line-color': 'rgb(0, 0, 0)',
        },
      };
  }
};

const polygonFillLayer: LayerProps = {
  id: 'feat-fill',
  type: 'fill',
  source: POLYGONS_SOURCE_ID,
  paint: {
    'fill-color': ['get', 'color'],
    'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.7, 0.6],
  },
};

const clusterLayer: LayerProps = {
  id: 'clusters',
  type: 'circle',

  source: MARKERS_SOURCE_ID,
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': '#ffffff',
    'circle-stroke-width': 15,
    'circle-stroke-color': '#ffffff',
    'circle-stroke-opacity': 0.4,
    'circle-radius': 20,
  },
};

const clusterCountLayer: LayerProps = {
  id: 'cluster-count',
  type: 'symbol',
  source: MARKERS_SOURCE_ID,
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-size': 12,
  },
};

const unclusteredPointLayer: LayerProps = {
  id: 'unclustered-point',
  type: 'circle',
  source: MARKERS_SOURCE_ID,
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-color': '#ffffff',
    'circle-stroke-width': 10,
    'circle-stroke-color': '#ffffff',
    'circle-stroke-opacity': 0.4,
    'circle-radius': 15,
  },
};

const iconLayer: LayerProps = {
  id: 'unclustered-point-icon',
  type: 'symbol',
  source: MARKERS_SOURCE_ID,
  filter: ['!', ['has', 'point_count']],
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-size': 0.5,
    'text-field': ['get', 'name'],
    'text-offset': [0, 3],
    'text-anchor': 'bottom',
    'text-size': 12,
  },
  paint: {
    'icon-color': ['case', ['get', 'selected'], '#0000FF', '#000000'],
    'text-color': '#ffffff',
    'text-halo-color': '#000000',
    'text-halo-width': 2,
  },
};
